import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useMemo, useState } from "react";
import { useVenue } from "../../shell/VenueContext";
import {
  type CreateUploadInput,
  type MediaAssetView,
  type MediaClient,
  type MediaPurpose,
  MEDIA_PURPOSES,
  acceptAttr,
  createMediaClient,
  formatBytes,
  isIndexedPurpose,
  isSubmitBlocked,
  quotaPercent,
  rejectionReasonKey,
  statusTone,
} from "./mediaApi";

const PENDING_STATES = new Set(["RECEIVED", "VALIDATED", "SCREENED", "PROCESSED"]);

export interface MediaManagerProps {
  /** Injected in tests; defaults to the venue-scoped authed client. */
  clientFactory?: (venueId: string) => MediaClient;
}

/**
 * The venue media manager (media-uploads): per-purpose upload with a mandatory alt-text/decorative
 * field that blocks submit (R6), a live quota meter (R3 guidance when full), status chips polled off
 * the asset list, and replace/retire. The presigned-PUT → confirm flow runs client-side; the pipeline
 * (screen → strip/re-encode → publish) runs server-side and the terminal state comes back on confirm.
 */
export function MediaManager({ clientFactory = createMediaClient }: MediaManagerProps) {
  const { t } = useTranslation();
  const { venueId } = useVenue();
  const client = useMemo(() => clientFactory(venueId), [clientFactory, venueId]);
  const queryClient = useQueryClient();

  const purposeId = useId();
  const positionId = useId();
  const altId = useId();
  const fileId = useId();

  const [purpose, setPurpose] = useState<MediaPurpose>("COVER");
  const [position, setPosition] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [altText, setAltText] = useState("");
  const [decorative, setDecorative] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assets = useQuery({
    queryKey: ["venue", venueId, "media"],
    queryFn: () => client.list(),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((a: MediaAssetView) => PENDING_STATES.has(a.state))
        ? 2000
        : false,
  });
  const quota = useQuery({
    queryKey: ["venue", venueId, "media", "quota"],
    queryFn: () => client.quota(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["venue", venueId, "media"] });
  };

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("no file");
      const input: CreateUploadInput = {
        purpose,
        position: isIndexedPurpose(purpose) ? position : null,
        contentType: file.type,
        sizeBytes: file.size,
        altText: altText.trim() || null,
        altDecorative: decorative,
      };
      const presign = await client.createUpload(input);
      await client.uploadBytes(presign.uploadUrl, file);
      return client.confirm(presign.assetId);
    },
    onSuccess: () => {
      setFile(null);
      setAltText("");
      setDecorative(false);
      setError(null);
      invalidate();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
  });

  const retire = useMutation({
    mutationFn: (assetId: string) => client.retire(assetId),
    onSuccess: invalidate,
  });

  const blocked = isSubmitBlocked({ file, altText, decorative, uploading: upload.isPending });
  const used = quota.data?.usedBytes ?? 0;
  const total = quota.data?.quotaBytes ?? 0;
  const pct = quotaPercent(used, total);
  const quotaFull = total > 0 && used >= total;

  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "6", maxW: "3xl" })}>
      <h2 className={css({ fontSize: "lg", fontWeight: "semibold" })}>{t("media.title")}</h2>

      {/* --- quota meter (R3) ---------------------------------------------------------------- */}
      <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
        <div className={css({ display: "flex", justifyContent: "space-between", fontSize: "sm" })}>
          <span>{t("media.quota.label")}</span>
          <span className={css({ color: "fg.muted" })}>
            {formatBytes(used)} / {formatBytes(total)}
          </span>
        </div>
        <progress
          value={pct}
          max={100}
          aria-label={t("media.quota.label")}
          className={css({
            w: "full",
            h: "2",
            rounded: "full",
            accentColor: quotaFull ? "fg.error" : "colorPalette.default",
          })}
        />
        {quotaFull ? (
          <output className={css({ display: "block", fontSize: "sm", color: "fg.error" })}>
            {t("media.quota.full")}
          </output>
        ) : null}
      </div>

      {/* --- upload form -------------------------------------------------------------------- */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!blocked) upload.mutate();
        }}
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "3",
          p: "4",
          borderWidth: "1px",
          rounded: "md",
        })}
      >
        <div className={css({ display: "flex", gap: "3", flexWrap: "wrap" })}>
          <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
            <label htmlFor={purposeId} className={css({ fontSize: "sm" })}>
              {t("media.purpose")}
            </label>
            <select
              id={purposeId}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value as MediaPurpose)}
              className={css({ px: "3", py: "2", borderWidth: "1px", rounded: "md" })}
            >
              {MEDIA_PURPOSES.map((p) => (
                <option key={p} value={p}>
                  {t(`media.purpose.${p}`)}
                </option>
              ))}
            </select>
          </div>
          {isIndexedPurpose(purpose) ? (
            <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
              <label htmlFor={positionId} className={css({ fontSize: "sm" })}>
                {t("media.position")}
              </label>
              <input
                id={positionId}
                type="number"
                min={0}
                value={position}
                onChange={(e) => setPosition(Math.max(0, Number(e.target.value)))}
                className={css({ px: "3", py: "2", borderWidth: "1px", rounded: "md", w: "24" })}
              />
            </div>
          ) : null}
        </div>

        <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
          <label htmlFor={fileId} className={css({ fontSize: "sm" })}>
            {t("media.file")}
          </label>
          <input
            id={fileId}
            type="file"
            accept={acceptAttr()}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
          <label htmlFor={altId} className={css({ fontSize: "sm" })}>
            {t("media.altText")}
          </label>
          <input
            id={altId}
            type="text"
            value={altText}
            disabled={decorative}
            placeholder={t("media.altText.placeholder")}
            onChange={(e) => setAltText(e.target.value)}
            className={css({ px: "3", py: "2", borderWidth: "1px", rounded: "md" })}
          />
          <label
            className={css({ display: "flex", gap: "2", alignItems: "center", fontSize: "sm" })}
          >
            <input
              type="checkbox"
              checked={decorative}
              onChange={(e) => setDecorative(e.target.checked)}
            />
            {t("media.decorative")}
          </label>
          <p className={css({ fontSize: "xs", color: "fg.muted" })}>{t("media.altText.help")}</p>
        </div>

        {error ? (
          <output className={css({ display: "block", fontSize: "sm", color: "fg.error" })}>
            {error}
          </output>
        ) : null}

        <button
          type="submit"
          disabled={blocked}
          className={css({
            alignSelf: "flex-start",
            px: "4",
            py: "2",
            rounded: "md",
            bg: "colorPalette.default",
            color: "colorPalette.fg",
            _disabled: { opacity: "0.5", cursor: "not-allowed" },
          })}
        >
          {upload.isPending ? t("media.uploading") : t("media.upload")}
        </button>
      </form>

      {/* --- library ------------------------------------------------------------------------ */}
      <ul
        className={css({ display: "flex", flexDirection: "column", gap: "2", listStyle: "none" })}
      >
        {(assets.data ?? []).map((asset) => (
          <li
            key={asset.id}
            className={css({
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "3",
              p: "3",
              borderWidth: "1px",
              rounded: "md",
            })}
          >
            <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
              <span className={css({ fontWeight: "medium" })}>
                {t(`media.purpose.${asset.purpose}`)}
                {asset.position !== null ? ` #${asset.position}` : ""}
              </span>
              <span className={css({ fontSize: "sm", color: "fg.muted" })}>
                {asset.altDecorative ? t("media.decorative") : (asset.altText ?? "")}
              </span>
              {asset.state === "REJECTED" ? (
                <span className={css({ fontSize: "sm", color: "fg.error" })}>
                  {t(rejectionReasonKey(asset.rejectedStage))}
                </span>
              ) : null}
              {asset.qualityFlag ? (
                <span className={css({ fontSize: "xs", color: "fg.warning" })}>
                  {t("media.lowQuality")}
                </span>
              ) : null}
            </div>
            <div className={css({ display: "flex", gap: "2", alignItems: "center" })}>
              <StatusChip
                state={asset.state}
                label={t(`media.status.${statusTone(asset.state)}`)}
              />
              {asset.state === "LIVE" ? (
                <button
                  type="button"
                  onClick={() => retire.mutate(asset.id)}
                  className={css({
                    px: "2",
                    py: "1",
                    fontSize: "sm",
                    borderWidth: "1px",
                    rounded: "md",
                  })}
                >
                  {t("media.retire")}
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StatusChip({ state, label }: { state: string; label: string }) {
  const tone = statusTone(state);
  const color =
    tone === "live"
      ? "fg.success"
      : tone === "rejected"
        ? "fg.error"
        : tone === "retired"
          ? "fg.muted"
          : "fg.warning";
  return <span className={css({ fontSize: "xs", fontWeight: "medium", color })}>{label}</span>;
}
